import CookiesBlockedPlaceholder from '@components/cookies-blocked-placeholder/cookies-blocked-placeholder';
import { useCookieConsent } from '@hooks/cookieConsent.ts/useCookieConsent';
import SvgClose from '@icons/SvgClose';
import React, { useEffect, useRef, useState } from 'react';

export interface IVideoPlayerProps {
    twentyThreeVideoPlayerCode?: string;
    size?: 'Medium' | 'Large';
    header?: string;
    abstract?: string;
    videoFunctionCookieRequirementText?: string;
    videoFunctionCookieRequirementButtonText?: string;
    videoCookiePlaceholderIconUrl?: string;
    guid: string;
    videoIsPlaying?: boolean;
    hideCookiePlaceholder?: boolean;
    onCloseCallback?: () => void;
}

export default function VideoPlayer({
    twentyThreeVideoPlayerCode = '',
    size = 'Medium',
    header,
    abstract,
    videoFunctionCookieRequirementText,
    videoFunctionCookieRequirementButtonText,
    videoCookiePlaceholderIconUrl,
    videoIsPlaying = false,
    guid,
    hideCookiePlaceholder,
    onCloseCallback,
}: IVideoPlayerProps) {
    const [isOverlayOpen, setIsOverlayOpen] = useState(videoIsPlaying);
    const cookieConsent = useCookieConsent();
    const isMobile = matchMedia('(max-width: 1024px)').matches;
    const useOverlay = size === 'Medium' && !isMobile;
    const overlayCloseButtonRef = useRef<HTMLButtonElement>(null);

    const onFocusSetCloseButtonFocus = () => {
        overlayCloseButtonRef.current?.focus();
    };

    useEffect(() => {
        if (isOverlayOpen) {
            overlayCloseButtonRef.current?.focus();
            document.addEventListener('keyup', handleKeyUp);
        }

        return () => {
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [isOverlayOpen]);

    const handleKeyUp = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            closeOverlay();
        }
    };

    const closeOverlay = () => {
        setIsOverlayOpen(false);
        if (typeof onCloseCallback === 'function') {
            onCloseCallback();
        }
    };

    if (!useOverlay && !isOverlayOpen) {
        const videoSrc = `${twentyThreeVideoPlayerCode}&autoMute=1&autoPlay=0&controls=1`;
        return (
            <article className={`fa-video-player ${size === 'Medium' ? 'fa-block-narrow' : 'fa-block'}`}>
                <h2 className="fa-video-player__title">{header}</h2>

                {!cookieConsent && (
                    <CookiesBlockedPlaceholder
                        text={videoFunctionCookieRequirementText ?? ''}
                        buttonText={videoFunctionCookieRequirementButtonText ?? ''}
                        icon={videoCookiePlaceholderIconUrl ?? ''}
                    />
                )}
                <div className="fa-video-player__container">
                    <iframe
                        id={guid}
                        src={cookieConsent ? videoSrc : undefined}
                        width="100%"
                        height="100%"
                        allowFullScreen={true}
                        allow="fullscreen"
                        data-category-consent="cookie_cat_functional"
                        data-consent-src={videoSrc}
                    ></iframe>
                </div>
                <p className="fa-video-player__abstract">{abstract}</p>
            </article>
        );
    }

    if (!cookieConsent && !hideCookiePlaceholder && !videoIsPlaying && !isOverlayOpen) {
        return (
            <CookiesBlockedPlaceholder
                text={videoFunctionCookieRequirementText ?? ''}
                buttonText={videoFunctionCookieRequirementButtonText ?? ''}
                icon={videoCookiePlaceholderIconUrl ?? ''}
            />
        );
    }

    // Desktop Video Player with Overlay
    return (
        <article className={`fa-video-player ${size === 'Medium' ? 'fa-block-narrow' : 'fa-block'}`}>
            <h2 className="fa-video-player__title">{header}</h2>
            <div className="fa-video-player__container">
                {isOverlayOpen && (
                    <div role="dialog" className={`fa-video-overlay ${isOverlayOpen ? 'fa-video-overlay--shown' : ''}`}>
                        <div className="fa-video-overlay__content fa-container">
                            <div className={`fa-video-player ${size === 'Medium' ? 'fa-block-narrow' : 'fa-block'}`}>
                                <div className="fa-video-overlay__content__header">
                                    {header && <h2 className="fa-video-overlay__content__header__title">{header}</h2>}
                                    <button
                                        ref={overlayCloseButtonRef}
                                        onClick={closeOverlay}
                                        className="fa-video-overlay__content__header__close"
                                        aria-label="Close overlay"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Tab' && e.shiftKey) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <SvgClose />
                                    </button>
                                </div>
                                <div className="fa-video-player__container">
                                    <iframe
                                        id={guid}
                                        src={
                                            cookieConsent
                                                ? `${twentyThreeVideoPlayerCode}&autoMute=1&autoPlay=1&controls=1`
                                                : undefined
                                        }
                                        width="100%"
                                        height="100%"
                                        allowFullScreen={true}
                                        allow="autoplay; fullscreen"
                                    ></iframe>
                                    <button
                                        className="tab-trap-button-hidden"
                                        onFocus={onFocusSetCloseButtonFocus}
                                    ></button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!videoIsPlaying && (
                    <div
                        className="fa-video-player__container__effect"
                        onClick={() => setIsOverlayOpen(true)}
                        aria-haspopup="true"
                    >
                        <iframe
                            className="fa-video-player__container__iframe--placeholder"
                            src={twentyThreeVideoPlayerCode}
                            tabIndex={-1}
                        ></iframe>
                    </div>
                )}
            </div>
            <p className="fa-video-player__abstract">{abstract}</p>
        </article>
    );
}
